<script>
    import {createEventDispatcher} from 'svelte';
    import {writable} from 'svelte/store';
    import {quintOut} from 'svelte/easing';
    import {onMount, onDestroy} from 'svelte';
    import {crossfade} from 'svelte/transition';
    import {flip} from 'svelte/animate';
    import {validateInput} from '../Form/utils.js';
    import GroupedOptions from './GroupedOptions.svelte';
    import Options from './Options.svelte';
    import OptionItem from './OptionItem.svelte';
    import { text } from 'svelte/internal';
    import {sgettext} from '../../i18n.js';

    export let id = 'comboSelect';
    export let key = '';
    export let label = '';
    export let tooltip = '';
    export let placeholder = sgettext('$$CLICK_SELECT_$$');
    export let searchPlaceholderText = sgettext('$$TYPE_SEARCH$$');
    export let searchTerm = '';
    export let multiple = false;
    export let pill = false;
    export let options = [];
    export let value = multiple ? [] : null;
    export let selected = [];
    export let hidePopup = true;
    export let children = '';
    export let required = false;
    export let isValid = true;
    export let schema = null;
    export let errors = '';
    export let searchable = false;
    export let autoHeight = true;
    export let isContextual = false;
    export let editable = true;
    export let riskValid = true;
    export let hazardText = '';

    if (!key) {
        key = id;
    }

    const dispatch = createEventDispatcher();

    $: results = Array.isArray(options) ? [...options] : [];

    let touched = false;

    $: value;

    $: if (value && results.length) {
        selected = children ? getSelectedChildren() : getSelected();
    } else if (!selected || selected.length === 0) {
        selected = '';
    } else {
        selected = '';
    }

    $: if (touched && hidePopup) {
        dispatch('changed', {
            value,
            key
        });

        resetSearch();
    }

    const placeholderMessages = [
        sgettext('$$CLICK_BEGIN_SELECTION$$'),
        sgettext('$$CLICK_COMPLETE_SELECTION$$'),
    ];

    const [send, receive] = crossfade({
        duration: (d) => Math.sqrt(d * 500),
        fallback(node, params) {
            const style = getComputedStyle(node);
            const transform = style.transform === 'none' ? '' : style.transform;

            return {
                duration: 500,
                easing: quintOut,
                css: (t) => `transform: ${transform} scale(${t});
          opacity: ${t}`,
            };
        },
    });

    function getSelected() {
        let found = [];

        const f = results.filter((c) => {
            if (Array.isArray(value)) {
                c.selected = value.includes(c.id);
                return c.selected;
            } else {
                return c.id === value;
            }
        });

        found = [...found, ...f];

        return multiple ? found : found[0];
    }

    function getSelectedChildren() {
        let found = [];

        results.forEach((g) => {
            const f = g[children].filter((c) => {
                if (Array.isArray(value)) {
                } else {
                    return c.id === value;
                }
            });

            found = [...found, ...f];
        });

        return multiple ? found : found[0];
    }

    function wrapperOnClick(event) {
        //hidePopup = !hidePopup;
    }

    function resetSearch() {
        searchTerm = '';
        search();
    }

    function cycleSelection(cycleBy) {
        if (multiple) {

        } else {
            let selectedIndex = 0;
            if (value) {
                selectedIndex = options.findIndex(i => i.id === value);
            }
            let temp = selectedIndex + cycleBy;
            if (temp < 0) {
                temp = 0;
            } else if (temp > options.length - 1) {
                temp = options.length - 1;
            }
            value = options[temp].id;
            selected = options[temp];
            dispatch('changed', {
                value,
                selected,
                key
            });
        }
    }

    function handleKeyUp(event) {
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowRight':
                cycleSelection(1);
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
                cycleSelection(-1);
                break;
            default:
                break;
        }
    }

    function handleFocus(event) {
        window.addEventListener("keydown", arrowKeysHandler, false);
    }

    function handleLostFocus(event) {
        window.removeEventListener('keydown', arrowKeysHandler, false);
    }

    function rotate(node, params) {
        return {
            delay: params.delay || 0,
            duration: params.duration || 200,
            css: (t, u) => `transform: rotate(${t * 180}deg)`,
        };
    }

    function handleKeyPress(event) {
        if (event.keyCode === 13) {
            event.preventDefault();
        }
    }

    function search(event) {
        const regex = new RegExp(searchTerm, 'gi');

        if (children) {
            groupedSearch(regex);
        } else {
            flatSearch(regex);
        }

        results = [...results];
    }

    function flatSearch(regex) {
        results.forEach((c) => {
            if (
                    c.name.match(regex) ||
                    (c.hasOwnProperty('description') && c.description.match(regex))
            ) {
                c.hidden = false;
            } else {
                c.hidden = true;
            }
        });
    }

    function groupedSearch(regex) {
        results.forEach((item) => {
            item.hidden = !item.name.match(regex);

            // if match on parent, show all children
            if (!item.hidden && item[children]) {
                item[children].forEach((c) => {
                    c.hidden = false;
                });
            }
            else if (item.hidden && item[children]) {
                // if match on child, show parent
                item[children].forEach((c) => {
                    if (
                            c.name.match(regex) ||
                            (c.hasOwnProperty('description') &&
                                    c.description.match(regex))
                    ) {
                        c.hidden = false;
                        item.hidden = false;
                    } else {
                        c.hidden = true;
                    }
                });
            }
        });
    }

    function highlight(text) {
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        if (searchTerm) {
            text = encodeURI(text)
                    .replace(new RegExp(/%20/gim), ' ')
                    .replace(new RegExp(/%C2%A0/gim), ' ')
                    .replace(regex, '<strong>$1</strong>');
        }
        return text;
    }

    function removeSelection(item, group) {
        item.selected = false;
        results = [...results];

        value = value.filter((v) => v != item.id);
        dispatch('changed');
    }


    function arrowKeysHandler(event) {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowLeft":
                event.preventDefault();
                break;
            case "ArrowDown":
            case "ArrowRight":
                event.preventDefault();
                break;
            default:
                break;
        }
    }

    function handleDocumentOnClick(event) {
        const element = document.getElementById(id);

        const found = event && event.path && event.path.filter((elm) => {
            return `${id}Select` === elm.id;
        }) || [];

        if (found.length === 0) {
            hidePopup = true;

            if (touched) {
                validate();
                resetSearch();
            }
        }
    }

    function validate() {
        if (schema) {
            if (!multiple) {
                validateInput(schema, key, value)
                        .then(() => {
                            isValid = true;
                        })
                        .catch((err) => {
                            isValid = false;
                            errors = err.message;
                        });
            }
            touched = false;
        }
    }

    onMount(() => {
        document.addEventListener('click', handleDocumentOnClick);
    });

    onDestroy(() => {
        document.removeEventListener('click', handleDocumentOnClick);
    });

    function handleSearchInputOnClick(event) {
        hidePopup = false;
        touched = true;
    }

    function handleCaretOnClick(event) {
        hidePopup = !hidePopup;
        touched = true;
    }

    $: {
        if (hidePopup) {
            dispatch('focusOut');
        }
    }
</script>

{#if label}
  <label for={id} title={required ? sgettext('$$REQUIRED_FIELD$$') : ''}>
    {label}
    <slot name="labelIcon" />
    {#if tooltip}
      <a
        href="#"
        data-toggle="tooltip"
        title={tooltip}
        style="color:#ccc; margin-left:5px;"
      >
        <i class="fas fa-question-circle" />
      </a>
    {/if}
  </label>
{/if}

{#if riskValid && hazardText === 'globalHazard'}
  <i
    class="fa fa-exclamation-circle text-danger"
    style="display: flex;margin-top: 14px;margin-left: -17px;margin-bottom: -30px"
  />
{/if}
<div
  id={`${id}Select`}
  class="combo-select"
  on:keyup|stopPropagation|preventDefault={handleKeyUp}
>
  {#if multiple}
    {#if pill}
      {#each selected as item}
        <div id={item.id} class="badge badge-pill badge-secondary mr-1 mb-1">
          <span on:click={() => removeSelection(item, results)}>
            {item.name}
            {#if editable}
              <i class="fa fa-times pill-remove" aria-hidden="true" />
            {/if}
          </span>
        </div>
      {/each}
    {/if}
    <div class="selected-options" class:hidden={!value || value.length === 0}>
      {#if !pill}
        <ul>
          {#each selected as item}
            <li id={item.id}>
              {#if item.hasOwnProperty('icon')}
                <div class="item-icon">
                  {#if item.icon}
                    <img src={item.icon.url} alt={item.icon.name} />
                  {/if}
                </div>
              {/if}
              <div class="item-name">
                <slot name="item-name" {item}><div>{item.name}</div></slot>

                {#if item.hasOwnProperty('description')}
                  <div style="white-space: pre-line">
                    {@html item.description}
                  </div>
                {/if}
              </div>
              {#if editable}
                <div
                  class="icon-remove"
                  on:click={() => removeSelection(item, results)}
                >
                  <i class="fa fa-times" aria-hidden="true" />
                </div>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
  {#if editable}
    <div
      class="wrapper"
      tabindex="0"
      on:focus={handleFocus}
      on:blur={handleLostFocus}
      class:invalid={!isValid}
      class:fix-height={!autoHeight}
      class:contextual={isContextual}
      on:click={wrapperOnClick}
    >
      <input {id} name={id} type="hidden" bind:value />
      {#if !selected || selected.length === 0 || multiple || !hidePopup}
        <div class="placeholder" data-testid="input-container">
          {#if searchable}
            <div class="search-input" data-testid="input-field">
              <input
                type="text"
                bind:value={searchTerm}
                on:keyup={search}
                on:keypress={handleKeyPress}
                on:blur={() => (touched = true)}
                on:click|stopPropagation={handleSearchInputOnClick}
                placeholder={searchPlaceholderText}
              />
            </div>
          {:else}
            <div on:click|stopPropagation={handleSearchInputOnClick}>
              {placeholder}
            </div>
          {/if}
        </div>
      {/if}

      {#if selected && !multiple && hidePopup}
        <div class="selected" on:click={handleSearchInputOnClick}>
          {#if selected.icon}
            <div class="icon">
              <img
                style="height: 50px; width: 50px;"
                src={selected.icon.url ? selected.icon.url : ''}
                alt={selected.name ? selected.name : ''}
              />
            </div>
          {:else if selected.faIcon}
            <div class="fa-icon">
              <i
                class={selected.faIcon.classes}
                style={selected.faIcon.style}
              />
            </div>
          {/if}
          <div class="label">
            <div data-testid="selected-name">{selected.name ? sgettext(selected.name) : ''}</div>
            {#if selected.description}
              <div class="text-secondary" style="white-space: pre-line" data-testid="selected-description">
                {selected.description}
              </div>
            {/if}
          </div>
        </div>
      {/if}
      <div class="button" on:click|stopPropagation={handleCaretOnClick}>
        <i
          class="fa fa-caret-down"
          class:show-popup={!hidePopup}
          class:hide-popup={hidePopup}
          aria-hidden="true"
          data-testid="arrow-button"
        />
      </div>
    </div>

    <div
      class="popup"
      class:hidden={hidePopup || results.length === 0}
      class:drop-down={!hidePopup}
      data-testid="popup-container"
    >
      {#if children}
        <GroupedOptions
          bind:options={results}
          {multiple}
          {children}
          {highlight}
          bind:value
          bind:hidePopup
        />
      {:else}
        <Options
          bind:options={results}
          {multiple}
          {highlight}
          bind:value
          bind:hidePopup
        />
      {/if}
    </div>
  {/if}
</div>
{#if !isValid}
  <div class="errors" data-testid="combobox-errors">{errors}</div>
{/if}

<style>
  .combo-select {
    min-width: 200px;
    min-height: 44px;
    position: relative;
  }

  .placeholder {
    position: relative;
    flex: 1;
    color: #a5a5a5;
  }

  .selected-options {
    border: 1px solid #dddddd;
    border-bottom: none;
    color: #495057;
  }

  .selected {
    flex: 1;
    display: flex;
    color: #000000;
  }

  .selected .label {
    flex: 1;
    line-height: 18px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .selected-options ul {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .selected-options li {
    padding: 5px 12px;
    line-height: 18px;
    display: flex;
  }

  .selected-options li:nth-child(even) {
    background-color: rgba(200, 200, 200, 0.2);
  }

  .item-icon {
    margin-right: 15px;
    width: 64px;
  }

  .wrapper.invalid {
    border: 1px solid #dc3545;
  }

  .errors {
    color: #dc3545;
    font-size: 13px;
  }

  .item-icon img {
    font-size: 10px;
    width: 100%;
  }

  .item-name {
    flex: 1;
    padding-right: 15px;
  }

  .item-name > div:first-child {
    color: #000000;
  }

  .icon-remove {
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .icon-remove:hover {
    color: #dd5900;
  }

  .pill-remove:hover {
    color: #666;
  }

  input {
    width: 100%;
    border: 1px solid #dddddd;
    padding: 0 6px;
    border: none;
  }

  .popup {
    border: 1px solid #dddddd;
    border-top: none;
    position: absolute;
    background-color: white;
    z-index: 1;
    width: 100%;
  }

  .wrapper {
    display: flex;
    border: 1px solid #dddddd;
    cursor: pointer;
    padding: 5px 12px;
    min-height: 44px;
    line-height: 30px;
    background-color: white;
  }

  .fa-icon {
    margin-right: 15px;
  }

  .wrapper.fix-height {
    height: 44px;
    overflow: hidden;
  }

  .wrapper.contextual {
    background-color: #bce5ff !important;
    border-color: #40a0dc !important;
    color: #00427b !important;
  }

  .button {
    display: flex;
    align-items: center;
  }

  .wrapper:hover {
    color: #dd5900;
  }

  :global(.options strong) {
    color: #dd5900;
  }

  .hidden {
    display: none;
  }

  .selected .icon {
    min-width: 32px;
    max-width: 64px;
    margin-right: 8px;
    font-size: 8px;
    line-height: 10px;
  }

  .icon img {
    width: 100%;
  }

  .search-input input:focus {
    outline: none;
  }

  .show-popup {
    -webkit-animation: rotate-down 0.3s ease-in-out both;
    animation: rotate-down 0.3s ease-in-out both;
  }

  .hide-popup {
    -webkit-animation: rotate-up 0.3s ease-in-out both;
    animation: rotate-up 0.3s ease-in-out both;
  }

  .drop-down {
    -webkit-animation: slide-in-top 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)
      both;
    animation: slide-in-top 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  }

  /**
    * ----------------------------------------
    * animation rotate-center
    * ----------------------------------------
    */
  @-webkit-keyframes rotate-down {
    0% {
      -webkit-transform: rotate(0);
      transform: rotate(0);
    }
    100% {
      -webkit-transform: rotate(180deg);
      transform: rotate(180deg);
    }
  }

  @keyframes rotate-down {
    0% {
      -webkit-transform: rotate(0);
      transform: rotate(0);
    }
    100% {
      -webkit-transform: rotate(180deg);
      transform: rotate(180deg);
    }
  }

  @-webkit-keyframes rotate-up {
    0% {
      -webkit-transform: rotate(180deg);
      transform: rotate(180deg);
    }
    100% {
      -webkit-transform: rotate(0);
      transform: rotate(0);
    }
  }

  @keyframes rotate-up {
    0% {
      -webkit-transform: rotate(180deg);
      transform: rotate(180deg);
    }
    100% {
      -webkit-transform: rotate(0);
      transform: rotate(0);
    }
  }

  /**
    * ----------------------------------------
    * animation slide-in-top
    * ----------------------------------------
    */
  @-webkit-keyframes slide-in-top {
    0% {
      -webkit-transform: translateY(-30px);
      transform: translateY(-30px);
      opacity: 0;
    }
    100% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slide-in-top {
    0% {
      -webkit-transform: translateY(-30px);
      transform: translateY(-30px);
      opacity: 0;
    }
    100% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
